import React, { forwardRef, useId } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { PiMagnifyingGlassLight, PiXLight } from 'react-icons/pi';
import { fieldSurface, FOCUS_RING_ONLY } from './FieldShell';

/**
 * THE search field.
 *
 * Roughly two dozen pages hand-rolled this: a `relative` wrapper, an absolutely
 * positioned magnifier at `left-3 top-1/2 -translate-y-1/2`, an input with
 * `pl-10`, and — in about half of them — a clear button that only some
 * remembered to also fire `onChange('')`. They disagreed on height, on icon
 * size, and on whether clearing refocused the field.
 *
 * One control. The magnifier and the clear affordance are structural, the
 * surface comes from `fieldSurface()` like every other field, and clearing
 * always both resets the value and returns focus to the input.
 */

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'size' | 'type'> {
  value: string;
  /** Receives the raw string, not the event — search callers never want the event. */
  onChange: (value: string) => void;
  placeholder?: string;
  /** Hides the clear button even when there is a value. */
  hideClear?: boolean;
  /** Compact variant for toolbars — `h-8` and smaller text. */
  size?: 'small' | 'medium';
  disabled?: boolean;
  className?: string;
  /** Wrapper class, for width (`w-64`) and layout. Defaults to full width. */
  wrapperClassName?: string;
  'aria-label'?: string;
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      placeholder,
      hideClear = false,
      size = 'medium',
      disabled,
      className,
      wrapperClassName,
      id,
      'aria-label': ariaLabel,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation();
    const generatedId = useId();
    const inputId = id || generatedId;
    const innerRef = React.useRef<HTMLInputElement | null>(null);

    const setRefs = (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    };

    const compact = size === 'small';
    const showClear = !hideClear && !!value && !disabled;

    return (
      <div className={clsx('relative', wrapperClassName || 'w-full')}>
        <PiMagnifyingGlassLight
          aria-hidden="true"
          className={clsx(
            'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted',
            compact ? 'h-3.5 w-3.5' : 'h-4 w-4'
          )}
        />
        <input
          id={inputId}
          ref={setRefs}
          // `search` gives the browser its own clear affordance in some engines,
          // which would sit next to ours. `text` keeps the control ours alone.
          type="text"
          role="searchbox"
          aria-label={ariaLabel || t('Search')}
          value={value}
          disabled={disabled}
          placeholder={placeholder ?? t('Search')}
          onChange={(e) => onChange(e.target.value)}
          className={clsx(
            fieldSurface({ disabled }),
            compact ? 'h-8 pl-8 text-xs' : 'pl-9',
            showClear ? 'pr-9' : 'pr-3',
            className
          )}
          {...rest}
        />
        {showClear && (
          <button
            type="button"
            aria-label={t('Clear search')}
            onClick={() => {
              onChange('');
              innerRef.current?.focus();
            }}
            className={clsx(
              'absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5',
              'text-fg-muted hover:text-fg-strong transition-colors',
              FOCUS_RING_ONLY
            )}
          >
            <PiXLight className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
          </button>
        )}
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
